import { TRAINING_MANAGER_PERMISSION } from '@/constants';
import {
  ActiveUserData,
  CurrentUserApiResponse,
  LoggedInUserData,
  TrainingManagerPermission,
  UserPermissions,
} from '../../types/api.types';
import { getAuthClient } from '../../common/auth/authClient';
import { DataContext } from '../../types/data.types';
import { User, UserStatus } from './User';

export interface AccessLevel {
  level: number;
}

export interface AccessLevels {
  none: AccessLevel;
  user: AccessLevel;
  employee: AccessLevel;
  mananger: AccessLevel; // Note: keeping original typo for consistency
  organizationAdmin: AccessLevel;
}

export interface CurrentUserOptions {
  resolve: (user: CurrentUser) => void;
  reject: (error: unknown) => void;
  context: DataContext;
}

// Re-export for backwards compatibility
export type { ActiveUserData, CurrentUserApiResponse, LoggedInUserData, UserPermissions };

export class CurrentUser extends User {
  static accessLevel: AccessLevels = {
    none: {
      level: -1,
    },
    user: {
      level: 0,
    },
    employee: {
      level: 1,
    },
    mananger: {
      level: 2,
    },
    organizationAdmin: {
      level: 3,
    },
  };

  loggedInUser: LoggedInUserData | undefined;
  activeUser: ActiveUserData | undefined;
  isCurrent!: boolean;
  isDeveloper!: boolean;
  hasGroup!: boolean;
  loggedInUserEmployeeId: string | undefined;
  loggedInUserFirstName!: string;
  loggedInUserLastName!: string;
  loggedInUserFullName!: string;
  // The signed-in user's own address, even while impersonating: emailed exports are sent here.
  loggedInUserEmail!: string;
  loggedInUserHasJob!: boolean;
  loggedInUserJobTitle!: string;
  loggedInUserIsOrganizationAdmin!: boolean;
  loggedInUserIsBillingUser!: boolean;
  loggedInUserIsTeamLead!: boolean;
  loggedInUserDisplayJobTitle!: string;
  loggedInUserCurrentRoleStandardLibraryJobDefinitionId!: string;
  loggedInUserStatedOrganizationName!: string;
  loggedInUserOnboardingCompletedDate!: string;
  canChangeUser!: boolean;
  canChangeOrg!: boolean;
  impersonating!: boolean;
  hasDemoEmployee!: boolean;
  isAdmin!: boolean;
  isBillingUser!: boolean;
  accessLevel!: AccessLevel;
  hasAcceptedTerms!: boolean;
  isParticipant!: boolean;
  accessToken!: string | null;
  preferredLanguageCode?: string;
  organizationDefaultLanguageCode?: string;
  displayJobTitle?: string;
  currentRoleStandardLibraryJobDefinitionId?: string;
  currentRoleStandardLibraryJobName?: string;
  statedOrganizationName?: string;
  onboardingCompletedDate?: string;
  isMyKlayoUser?: boolean;
  isTrainingManager?: boolean;
  isFullAccess!: boolean;
  // True once this employee owns at least one personal (free-tier) job. Gates the Manage→Jobs nav.
  hasPersonalJobs?: boolean;
  hasActiveEnrollments?: boolean;
  trainingManagerPermissions?: TrainingManagerPermission[];
  statedCountry?: string;
  // Analytics fields for PostHog/HubSpot identify (see useAnalyticsIdentity).
  hasSkills?: boolean;
  hasPrepaidBalance?: boolean;
  unlockedFreeJob?: boolean;
  unlockedAnyJob?: boolean;
  careerRole?: string;
  // Public-profile slug; present only when the user's public profile is enabled.
  publicProfileSlug?: string;
  // Whether the user's photo is shown on their public profile.
  publicProfilePhotoEnabled?: boolean;
  displayName?: string;

  static instance: CurrentUser;

  constructor(options: CurrentUserOptions) {
    super({
      api: '/User',
      callback: () => {
        options.resolve(this);
        this.email = getAuthClient().getAccount()?.email ?? '';
      },
      ...options,
    });
  }

  onDataReceived(data: CurrentUserApiResponse): void {
    this.initCurrentUserData(data);
  }

  initCurrentUserData(data: CurrentUserApiResponse): void {
    this.loggedInUser = data.loggedInUser;
    this.activeUser = data.activeUser;

    super.initData(this.activeUser.basicInformation);

    this.isCurrent = true;
    this.isDeveloper = this.loggedInUser.permissions.isDeveloper || false;
    this.hasGroup = this.activeUser.basicInformation.hasGroup || false;

    if (this.loggedInUser) {
      this.loggedInUserEmployeeId = this.loggedInUser.basicInformation.employeeId;
      this.loggedInUserFirstName = this.loggedInUser.basicInformation.firstName || '';
      this.loggedInUserLastName = this.loggedInUser.basicInformation.lastName || '';
      this.loggedInUserFullName = this.loggedInUser.basicInformation.fullName || '';
      this.loggedInUserEmail = this.loggedInUser.basicInformation.email || '';
      this.loggedInUserHasJob = this.loggedInUser.basicInformation.hasJob || false;
      this.loggedInUserJobTitle = this.loggedInUser.basicInformation.jobTitle || '';
      this.loggedInUserIsOrganizationAdmin =
        this.loggedInUser.basicInformation.isOrganizationAdmin || false;
      this.loggedInUserIsBillingUser = this.loggedInUser.basicInformation.isBillingUser || false;
      this.loggedInUserIsTeamLead = this.loggedInUser.basicInformation.isTeamLead || false;

      this.canChangeUser = this.loggedInUser.permissions.canChangeUser || false;
      this.canChangeOrg = this.loggedInUser.permissions.canChangeOrg || false;

      this.impersonating =
        this.loggedInUser.basicInformation.employeeId !==
        this.activeUser.basicInformation.employeeId;
      this.hasDemoEmployee = this.loggedInUser.permissions.hasDemoEmployeeId || false;
      this.loggedInUserDisplayJobTitle = this.loggedInUser.basicInformation.displayJobTitle || '';
      this.loggedInUserCurrentRoleStandardLibraryJobDefinitionId =
        this.loggedInUser.basicInformation.currentRoleStandardLibraryJobDefinitionId || '';
      this.loggedInUserStatedOrganizationName =
        this.loggedInUser.basicInformation.statedOrganizationName || '';
      this.loggedInUserOnboardingCompletedDate =
        this.loggedInUser.basicInformation.onboardingCompletedDate || '';
    }
    this.isOrganizationAdmin = this.activeUser.basicInformation.isOrganizationAdmin || false;
    this.isTeamLead = this.activeUser.basicInformation.isTeamLead || false;
    this.isAdmin = this.isOrganizationAdmin || this.isTeamLead;
    this.isBillingUser = this.activeUser.basicInformation.isBillingUser || false;
    this.hasJob = this.activeUser.basicInformation.hasJob || false;
    const statusIndex = this.activeUser.basicInformation.status;
    this.status =
      statusIndex !== undefined
        ? (Object.values(User.status)[statusIndex] as UserStatus) || null
        : null;
    this.accessLevel = this.getAccessLevel();
    this.hasAcceptedTerms =
      this.activeUser.basicInformation.organizationAcceptTermsAndConditions || false;
    this.isInstructor = this.activeUser.basicInformation.isInstructor || false;
    this.isParticipant = this.activeUser.basicInformation.isParticipant || false;
    this.preferredLanguageCode = this.activeUser.basicInformation.preferredLanguageCode;
    this.organizationDefaultLanguageCode =
      this.activeUser.basicInformation.organizationDefaultLanguageCode;
    this.displayJobTitle = this.activeUser.basicInformation.displayJobTitle;
    this.currentRoleStandardLibraryJobDefinitionId =
      this.activeUser.basicInformation.currentRoleStandardLibraryJobDefinitionId;
    this.currentRoleStandardLibraryJobName =
      this.activeUser.basicInformation.currentRoleStandardLibraryJobName;
    this.statedOrganizationName = this.activeUser.basicInformation.statedOrganizationName;
    this.onboardingCompletedDate = this.activeUser.basicInformation.onboardingCompletedDate;
    this.isMyKlayoUser = this.activeUser.basicInformation.isMyKlayoUser;
    this.isTrainingManager = this.activeUser.basicInformation.isTrainingManager;
    this.isFullAccess = this.activeUser.basicInformation.isFullAccess ?? true;
    this.hasPersonalJobs = this.activeUser.basicInformation.hasPersonalJobs ?? false;
    this.hasActiveEnrollments = this.activeUser.basicInformation.hasActiveEnrollments;
    this.trainingManagerPermissions = this.activeUser.basicInformation.trainingManagerPermissions;
    this.isAllowEnrollStudents = this.activeUser.basicInformation.trainingManagerPermissions?.some(
      (p) => p.trainingManagerPermissionTypeId === TRAINING_MANAGER_PERMISSION.ENROLL
    );
    this.isAllowUnenrollStudents =
      this.activeUser.basicInformation.trainingManagerPermissions?.some(
        (p) => p.trainingManagerPermissionTypeId === TRAINING_MANAGER_PERMISSION.UNENROLL
      );

    this.statedCountry = this.activeUser.basicInformation.statedCountry;
    this.hasSkills = this.activeUser.basicInformation.hasSkills;
    this.hasPrepaidBalance = this.activeUser.basicInformation.hasPrepaidBalance;
    this.unlockedFreeJob = this.activeUser.basicInformation.unlockedFreeJob;
    this.unlockedAnyJob = this.activeUser.basicInformation.unlockedAnyJob;
    this.careerRole = this.activeUser.basicInformation.careerRole;
    this.publicProfileSlug = this.activeUser.basicInformation.publicProfileSlug;
    this.publicProfilePhotoEnabled = this.activeUser.basicInformation.publicProfilePhotoEnabled;
    this.displayName = this.activeUser.basicInformation.displayName;
  }

  getAccessLevel(): AccessLevel {
    if (!this.isActive()) return CurrentUser.accessLevel.none;
    if (this.isOrganizationAdmin) return CurrentUser.accessLevel.organizationAdmin;
    if (this.isTeamLead) return CurrentUser.accessLevel.mananger;
    if (this.hasJob) return CurrentUser.accessLevel.employee;
    return CurrentUser.accessLevel.user;
  }

  onGetToken(accessToken: string): void {
    this.accessToken = accessToken;
  }

  static getCurrentUser(
    context: DataContext,
    updateCache?: boolean
  ): Promise<{ data: CurrentUser; cached: boolean }> {
    return new Promise((resolve, reject) => {
      if (CurrentUser.instance && updateCache !== true)
        resolve({ data: CurrentUser.instance, cached: true });
      else
        CurrentUser.instance = new CurrentUser({
          resolve: (user: CurrentUser) => resolve({ data: user, cached: false }),
          reject,
          context,
        });
    });
  }

  hasAttribute(attribute: { attributeDefinitionId: string }): boolean {
    if (!this.profile) return false;
    return !!this.profile.hasAttribute(attribute);
  }

  hasAccess(level: AccessLevel | { level?: number }): boolean {
    return level.level === undefined || this.accessLevel.level >= level.level;
  }
}
